import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HomeIcon,
  PracticeTestIcon,
  DocumentIcon,
  TopicFocusIcon,
  UserIcon,
} from "components/atoms/Icon";
import { NavItems } from "components/molecules/NavItems";
import { Typography } from "components/atoms/Typography";
import mcatChatIcon from "assets/mcat-chat/mcat-chat-icon.svg";
import { useAuthenticateUserMutation } from "services/mcatChat/mcatChatExternal";
import { MCAT_TEST_TYPE } from "utils/constants";
import { decodeToken } from "utils/functions/decodeToken";
import { getOfficeHoursDisplayName } from "utils/functions/getOfficeHoursDisplayName";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  ai_mcat_chat_status,
  setIsShowACTPopup,
  setAIMCATChatStatus,
  setLayout,
  setToggleAiMCATDrawerExternally,
  setToggleMobileMenu,
  closeIframeModal,
} from "../../../state/reducers/layoutSlice";
import { useGetSettingsURLQuery } from "services/myAccount";
import { logOut } from "../../../state/reducers/AuthSlice";
import { PURGE } from "redux-persist";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
  isMobile: boolean;
  isTab: boolean;
  isDesktop: boolean;
  payload?: any;
}

export const NavBar = ({ isMobile, payload }: IProps) => {
  const [tabs, setTabs] = useState<any>([]);
  const [activeTab, setActiveTab] = useState(0);

  const dispatch: any = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isError } = useGetSettingsURLQuery();
  const token = decodeToken();
  const { mode } = useAppSelector((state) => state.theme);

  const { toggleMobileMenu, aiMCATChatDrawerStatus } = useAppSelector(
    (state: {
      layout: {
        toggleMobileMenu: boolean;
        aiMCATChatDrawerStatus: ai_mcat_chat_status;
      };
    }) => state.layout
  );

  const isShowAiTutor =
    token &&
    token.hasOwnProperty("showaitutor") &&
    token.showaitutor.toLowerCase() === "true" &&
    token.testtype === MCAT_TEST_TYPE;

  const [sendUserAuthRequest, { isSuccess: isSuccessAuthRequest }]: any = useAuthenticateUserMutation();

  useEffect(() => {
    if (isSuccessAuthRequest && aiMCATChatDrawerStatus === 'hidden') {
      dispatch(setLayout('toggle-desktop-menu'));
      dispatch(setAIMCATChatStatus('open'));
      dispatch(setToggleMobileMenu(false));
    }
  }, [isSuccessAuthRequest]);

  const handleAIMCATButtonClick = () => {
    if (aiMCATChatDrawerStatus === 'hidden') {
      // If hidden, authenticate and open
      sendUserAuthRequest();
    } else if (aiMCATChatDrawerStatus === 'open') {
      // If open, close the drawer (but keep it visible)
      dispatch(setAIMCATChatStatus('closed'));
      dispatch(setLayout(null));
    } else {
      // If closed, open the drawer
      dispatch(setAIMCATChatStatus('open'));
      dispatch(setLayout('toggle-desktop-menu'));
    }
  };

  useEffect(() => {
    setTabs([
      ...(payload &&
      payload.hasOwnProperty("showhomepage") &&
      payload.showhomepage.toLowerCase() === "true"
        ? [
            {
              id: 0,
              icon: <HomeIcon color={activeTab === 0 ? "#273E5A" : "#eee"} />,
              label:
                payload &&
                payload.hasOwnProperty("Home") &&
                payload.hasOwnProperty("Home") !== ""
                  ? payload.Home
                  : "Home",
              route: "/",
            },
          ]
        : []),
      ...(payload &&
      payload.hasOwnProperty("showpracticepage") &&
      payload.showpracticepage.toLowerCase() === "true"
        ? [
            {
              id: 1,
              icon: (
                <PracticeTestIcon
                  color={activeTab === 1 ? "#273E5A" : "#eee"}
                />
              ),
              label:
                payload &&
                payload.hasOwnProperty("Practice") &&
                payload.hasOwnProperty("Practice") !== ""
                  ? payload.Practice
                  : "Practice",
              route: "practice",
            },
          ]
        : []),
      ...(payload &&
      payload.hasOwnProperty("showcourseindex") &&
      payload.showcourseindex.toLowerCase() === "true"
        ? [
            {
              id: 2,
              icon: (
                <DocumentIcon color={activeTab === 2 ? "#273E5A" : "#eee"} />
              ),
              label:
                payload &&
                payload.hasOwnProperty("CourseIndex") &&
                payload.hasOwnProperty("CourseIndex") !== ""
                  ? payload.CourseIndex
                  : "Course Index",
              route: "course-index",
            },
          ]
        : []),
      ...(payload &&
      payload.hasOwnProperty("showsupplementalsessions") &&
      payload.showsupplementalsessions.toLowerCase() === "true"
        ? [
            {
              id: 3,
              icon: (
                <TopicFocusIcon color={activeTab === 3 ? "#273E5A" : "#eee"} />
              ),
              label:
                payload &&
                payload.hasOwnProperty("SupplementalSessions") &&
                payload.hasOwnProperty("SupplementalSessions") !== ""
                  ? payload.SupplementalSessions
                  : `${getOfficeHoursDisplayName(payload?.testtype || "")} Sessions`,
              route: "topic-focus",
            },
          ]
        : []),
      // My Account
      ...(data && !isError
        ? [
            {
              id: 4,
              icon: <UserIcon color={activeTab === 4 ? "#273E5A" : "#eee"} />,
              label: "My Account",
              route: "my-account",
              dropdown: {
                isVisible: tabs?.find((item: any) => item.id === 4)?.dropdown?.isVisible ?? false,
                routes: [
                  { id: 1, label: "Account Details" },
                  { id: 2, label: "My Courses" },
                  { id: 3, label: "Log Out" },
                ],
              },
            },
          ]
        : []),
    ]);
  }, [payload, activeTab, data, isError, isShowAiTutor, mode]);

  const handleLogout = () => {
    dispatch(logOut());
    dispatch({ type: PURGE, key: "auth", result: () => null });
    localStorage.clear();
    dispatch(setIsShowACTPopup(false));
    window.location.href = `${process.env.REACT_APP_RETURN_URL}`;
  };

  const handleNavigation = (
    tabId: number | string,
    route: string,
    subRouteId?: number
  ) => {
    // Handle AI Tutor button click (don't close modal for AI tutor)
    if (route === "ai-tutor") {
      handleAIMCATButtonClick();
      return;
    }

    // Close score report modal when navigating to other pages
    dispatch(closeIframeModal());

    if (route === "my-account") {
      if (subRouteId) {
        dispatch(setToggleMobileMenu(false));
        switch (subRouteId) {
          case 1:
            window.location.assign(data);
            break;
          case 2:
            process.env.REACT_APP_HOSTED_URL &&
              window.location.assign(process.env.REACT_APP_HOSTED_URL);
            break;
          case 3:
            handleLogout();
        }
      } else {
        setTabs(
          tabs.map((tab: any) => {
            if (tab.id === 4) {
              const updatedTab = { ...tab };
              updatedTab.dropdown.isVisible =
                aiMCATChatDrawerStatus === "open"
                  ? true
                  : !tab.dropdown.isVisible;
              return updatedTab;
            }
            return tab;
          })
        );
        if (aiMCATChatDrawerStatus === "open") {
          dispatch(setToggleAiMCATDrawerExternally(true));
        }
      }
    } else {
      dispatch(setToggleMobileMenu(false));
      if (typeof tabId === 'number') {
        setActiveTab(tabId);
      }
      navigate(route);
    }
  };

  useEffect(() => {
    switch (location.pathname) {
      case "/":
        setActiveTab(0);
        break;
      case "/practice":
        setActiveTab(1);
        break;
      case "/course-index":
        setActiveTab(2);
        break;
      case "/topic-focus":
        setActiveTab(3);
        break;
      default:
        break;
    }
  }, [location.pathname]);

  return (
    <div className="nav-container">
      <nav className="nav-wrapper">
        <ul>
          {tabs.map((tab: any) => (
            <li key={tab.id} onClick={() => handleNavigation(tab.id, tab.route)}>
              <div
                className={`nav-item-wrapper ${
                  !tab.isAiTutor && activeTab === tab.id ? 'active' : ''
                }`}
              >
                {tab.icon}
                <Typography
                  styles="nav-item-label"
                  variant="h6Medium"
                  label={tab.label}
                />
              </div>
              {tab.dropdown?.isVisible && tab.dropdown.routes?.length > 0 && (
                <ul className='nav-sub-list'>
                  {tab.dropdown.routes.map((subRoute: any) => (
                    <li
                      key={`${tab.id}_${subRoute.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigation(tab.id, tab.route, subRoute.id)
                      }}
                    >
                      <Typography
                        variant="h7Med"
                        label={subRoute.label}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};
